import React from 'react';
import { Typography, Box, CircularProgress, IconButton } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import workAnalyzerService from '../../services/workAnalyzerService';

type InspectionEvaluationSummaryDisplayProps = {
  totalLowCost: number;
  totalHighCost: number;
  issueCount: number;
  score: number;    // Score from 1 to 100
  address: string;
  inspectionId: string; // ID of the inspection for fetching the document
};

// Calculate letter grade based on score
const getLetterGrade = (score: number) => {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 50) return 'C';
  if (score >= 30) return 'D';
  return 'F';
};

const getScoreColor = (score: number) => {
  if (score > 75) return '#4CAF50';    // High score - Green
  if (score > 50) return '#FFA500';    // Moderate score - Orange
  return '#FF4C4C';                    // Low score - Red
};

const InspectionEvaluationSummaryDisplay: React.FC<InspectionEvaluationSummaryDisplayProps> = ({
  inspectionId,
  totalLowCost,
  totalHighCost,
  issueCount,
  score,
  address,
}) => {
  const grade = getLetterGrade(score);

  // Function to handle document viewing
  const handleDocumentView = async () => {
    try {
      const documentUrl = await workAnalyzerService.getInspectionDocument(inspectionId);
      if (documentUrl) {
        window.open(documentUrl, '_blank');
      } else {
        console.error('Failed to retrieve document URL');
      }
    } catch (error) {
      console.error("Error fetching document URL:", error);
    }
  };

  return (
    <Box sx={{ mt: 4, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 4 }}>
      
      {/* Left Section: Address, Document Icon, and Cost Range */}
      <Box sx={{ textAlign: 'left' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" color="textPrimary" sx={{ fontWeight: 'bold' }}>
            {address}
          </Typography>
          <IconButton onClick={handleDocumentView} color="primary" aria-label="View Document">
            <DescriptionIcon />
          </IconButton>
        </Box>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
          Total Projected Cost Range
        </Typography>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
          ${totalLowCost.toLocaleString()} - ${totalHighCost.toLocaleString()}
        </Typography>
      </Box>

      {/* Right Section: Score, Grade, and Issue Count */}
      <Box sx={{ textAlign: 'center', position: 'relative' }}>
        <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
          Property Score
        </Typography>
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress
            variant="determinate"
            value={score}
            size={70}
            thickness={5}
            sx={{
              color: getScoreColor(score),
              transform: 'rotate(-90deg)',
            }}
          />
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <Typography variant="h6" color="textSecondary">
              {grade}
            </Typography>
          </Box>
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Number of Issues: {issueCount}
        </Typography>
      </Box>
    </Box>
  );
};

export default InspectionEvaluationSummaryDisplay;
